import { useEffect, useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { Dialog, ReasonDialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import { circleHelpers, circleOwner, canReassignCircle, type CircleOwner as Owner } from '../../domain/ownership';
import { isAdmin } from '../../domain/permissions';
import { displayName } from '../../domain/names';
import type { Account, PersonWithNames, UUID } from '../../domain/types';
import { useI18n } from '../../i18n';
import { useRepos } from '../../repositories';
import type { PersonRemovalView } from '../../repositories/types';
import { useApp } from '../../app/AppState';

/**
 * Who looks after this circle — and, for an administrator, the two things only
 * they can do about it.
 *
 * Every circle has an owner: the person themselves once they have claimed it,
 * otherwise whoever added them (domain/ownership.ts). Saying so is what makes
 * the rule visible — "why can't I change this photo?" has an answer on the
 * screen rather than in a permission error.
 *
 * It says it in one short row: a shield, then the faces of whoever may change
 * this circle. It used to be two full sentences ("Miriam Edry looks after this
 * circle", "Also managed by Sarah Edry-Katz") stacked on top of each other, and
 * on a phone that was two lines of reading for something you only glance at.
 * The sentences are still there for anyone hovering or listening — as the
 * row's label — but they no longer take up the card.
 *
 * And when the person keeps their own circle, with nobody helping, the row is
 * not there at all: the card is already their face and their name, so a line
 * repeating it says nothing. The row exists to answer "who else can change
 * this?", which only has an answer when somebody else can.
 *
 * An administrator in Admin mode can hand a circle to a different relative, and
 * can propose removing it — which no single administrator may do alone.
 */
export function CircleOwnerPanel({ person, className = '' }: { person: PersonWithNames; className?: string }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { actor, me, links, grants, people, reload, adminMode } = useApp();
  const { toast } = useAnnouncer();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [picking, setPicking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [openRemoval, setOpenRemoval] = useState<PersonRemovalView | null>(null);

  const admin = actor ? isAdmin(actor.session.role) : false;
  const owner: Owner | null = circleOwner(links, grants, person.id);
  const reassignable = canReassignCircle(links, person.id);
  // Helpers the family asked to manage this profile — they can change it too,
  // so the profile says who they are rather than leaving it a mystery.
  const helpers = circleHelpers(grants, person.id).filter((g) => g.helperAccountId !== owner?.accountId);

  // Everyone sees who looks after a circle, so everyone needs the names.
  useEffect(() => {
    void repos.auth.listAccounts().then(setAccounts);
  }, [repos]);

  // Only administrators need to know a removal is being discussed.
  useEffect(() => {
    if (!admin) {
      setOpenRemoval(null);
      return;
    }
    let cancelled = false;
    void repos.admin.listPersonRemovals().then((list) => {
      if (!cancelled) setOpenRemoval(list.find((r) => r.proposal.personId === person.id && r.proposal.status === 'open') ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [repos, admin, person.id, adminMode]);

  if (!actor) return null;

  const nameOf = (accountId: UUID): string => accounts.find((a) => a.id === accountId)?.displayName ?? t('common.unknown');
  const ownerLabel = () => {
    if (!owner) return t('owner.nobody');
    if (owner.accountId === actor.session.accountId) return owner.kind === 'self' ? t('owner.yourOwn') : t('owner.you');
    return owner.kind === 'self'
      ? t('owner.themselves', { name: displayName(person, lang) })
      : t('owner.by', { name: nameOf(owner.accountId) });
  };

  const assign = (accountId: UUID | null) => {
    setPicking(false);
    void repos.people.setCircleOwner(person.id, accountId).then((r) => {
      if (r.ok) {
        toast(t('owner.changed'));
        reload();
      } else toast(t(('messageKey' in r ? r.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
    });
  };

  /** Accounts that could look after this circle: everyone in the family, the current owner aside. */
  const candidates = accounts.filter((a) => a.id !== owner?.accountId);

  /** The circle a family account belongs to, so a keeper can be a face rather than a name. */
  const personOf = (accountId: UUID) => people.find((p) => links.some((l) => l.accountId === accountId && l.personId === p.id && l.status === 'active'));
  /**
   * A person who keeps their own circle is not worth a line: their face and
   * their name are already the whole card, and saying "Noa Edry looks after
   * this circle" under Noa Edry's picture is a sentence nobody needs to read.
   * So the row names only the *other* people who may change this circle — a
   * relative who looks after it, and any helpers — and disappears entirely
   * when there is nobody but the person themselves.
   */
  const keepers = [
    ...(owner && owner.kind !== 'self' ? [owner.accountId] : []),
    ...helpers.map((g) => g.helperAccountId),
  ];
  const selfKept = owner?.kind === 'self';
  const showKeepers = !selfKept || keepers.length > 0;
  // The full sentences, kept for the label: short on the screen, complete to a screen reader.
  const spoken = [ownerLabel(), helpers.length > 0 ? t('owner.helpers', { names: helpers.map((g) => nameOf(g.helperAccountId)).join(', ') }) : ''].filter(Boolean).join(' · ');

  return (
    <div className={`owner-line ${className}`}>
      {showKeepers && (
        <p className="owner-who" title={spoken} aria-label={spoken}>
          <Icon name="shield" />
          {keepers.length === 0 ? (
            <span className="owner-none">{t('owner.none')}</span>
          ) : (
            <span className="owner-faces">
              {keepers.map((accountId) => {
                const p = personOf(accountId);
                return p ? (
                  <Avatar key={accountId} person={p} lang={lang} size="sm" />
                ) : (
                  <span key={accountId} className="owner-face-blank" aria-hidden="true"><Icon name="person" /></span>
                );
              })}
              <span className="owner-name">{nameOf(keepers[0])}</span>
              {keepers.length > 1 && <span className="owner-more">+{keepers.length - 1}</span>}
            </span>
          )}
        </p>
      )}

      {admin && reassignable && (
        <div className="row owner-admin">
          <button type="button" className="btn btn-sm btn-secondary is-admin-control" onClick={() => setPicking(true)}>
            <Icon name="people" />
            <span>{t('owner.change')}</span>
          </button>
          {me?.id !== person.id && !openRemoval && (
            <button type="button" className="btn btn-sm btn-ghost is-admin-control" onClick={() => setRemoving(true)}>
              <Icon name="trash" />
              <span>{t('removal.propose')}</span>
            </button>
          )}
        </div>
      )}
      {/* A circle its own person has claimed cannot be handed to anyone; there is nothing to offer and nothing to explain. */}
      {admin && !reassignable && me?.id !== person.id && !openRemoval && (
        <div className="row owner-admin">
          <button type="button" className="btn btn-sm btn-ghost is-admin-control" onClick={() => setRemoving(true)}>
            <Icon name="trash" />
            <span>{t('removal.propose')}</span>
          </button>
        </div>
      )}

      {/* An open removal is the one thing about a circle every administrator should see on it. */}
      {openRemoval && (
        <p className="small admin-note">
          <Icon name="clock" />
          <span>{t('removal.pending', { yes: openRemoval.tally.approvals, needed: openRemoval.tally.needed })}</span>
        </p>
      )}

      <Dialog open={picking} onClose={() => setPicking(false)} title={t('owner.change')} labelledBy="owner-pick-title">
        <div className="stack">
          <p className="muted" style={{ marginTop: 0 }}>{t('owner.changeHelp')}</p>
          <ul className="list-reset stack">
            {candidates.map((a) => {
              const theirPerson = people.find((p) => links.some((l) => l.accountId === a.id && l.personId === p.id && l.status === 'active'));
              return (
                <li key={a.id}>
                  <button type="button" className="btn btn-secondary btn-block owner-choice" onClick={() => assign(a.id)}>
                    {theirPerson ? <Avatar person={theirPerson} lang={lang} size="sm" /> : <Icon name="person" />}
                    <span className="grow">{a.displayName}</span>
                  </button>
                </li>
              );
            })}
            {owner && (
              <li>
                <button type="button" className="btn btn-ghost btn-block" onClick={() => assign(null)}>
                  <Icon name="close" />
                  <span>{t('owner.clear')}</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      </Dialog>

      <ReasonDialog
        open={removing}
        title={t('removal.propose')}
        body={t('removal.proposeHelp')}
        confirmLabel={t('removal.propose')}
        onClose={() => setRemoving(false)}
        onConfirm={(reason) => {
          void repos.admin.proposePersonRemoval(person.id, reason).then((r) => {
            if (r.ok) {
              toast(t('removal.proposed'));
              reload();
              void repos.admin.listPersonRemovals().then((list) => setOpenRemoval(list.find((x) => x.proposal.personId === person.id && x.proposal.status === 'open') ?? null));
            } else toast(t(('messageKey' in r ? r.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
          });
        }}
      />
    </div>
  );
}
